<template>
  <div id="evaluation-panel" class="pt-5 pb-5 is-max-desktop">
    <EvaluationBreadcrumb :links="links" />
    <div v-if="!isLoadingEvaluation">
      <EvaluationHeader :debtor="debtor" />
      <main id="evaluation-steps" class="block mt-4">
        <header class="summary-header">
          <article id="summary-client">
            <b-tag
              v-if="evaluation && evaluation.applicationDate !== '' && evaluation.applicationDate !== null && evaluation.applicationDate !== '0000-00-00'"
              type="is-primary is-bg-transparent mr-4"
              icon="calendar-days"
              data-cy="birthdate">
              {{ formatDate(evaluation.applicationDate) }}
            </b-tag>
            <b-tag
              type="is-primary is-bg-transparent mr-4"
              icon="location-arrow"
              data-cy="simulationOrigin"
              rounded>
              {{ evaluation.simulationOrigin }}
            </b-tag>
            <b-tag
              type="is-primary is-bg-transparent mr-4"
              :icon="evaluation.type === 'automatic' ? 'robot' : 'circle-user'"
              data-cy="automatic"
              rounded>
              {{ evaluation.type === 'automatic' ? 'Automática' : 'Manual' }}
            </b-tag>
            <b-tag
              type="is-primary is-bg-transparent mr-4"
              :icon="evaluation.future ? 'hourglass-start' : 'bolt'"
              data-cy="future"
              rounded>
              {{ evaluation.future ? 'Futura' : 'Inmediata' }}
            </b-tag>
          </article>
          <article v-if="evidenceData.length > 0" class="evaluations-documents">
            <b-button
              v-for="(document, i) in evidenceData"
              :key="i"
              size="is-small"
              type="mr-4"
              icon-left="download"
              tag="a"
              :href="`data:application/pdf;base64,${document.pdfBase64}`"
              :download="document.pdfFileName"
              rounded>
              {{ document.pdfFileName }}
            </b-button>
          </article>
        </header>
        <section id="evaluations-resume" class="step-content box">
          <Resume :evaluation="evaluation" class=" mb-5" />
          <b-button
            id="btn-home"
            outlined
            tag="router-link"
            :to="{ name: 'evaluation-detail', params: { evaluationId: getParentEvaluationId() } }"
            class="is-secondary"
            type="is"
            icon-pack="fas"
            icon-left="chevron-left"
            data-cy="btn-home">
            Regresar
          </b-button>
          <b-button
            v-if="isReadyForRatify()"
            type="is-danger"
            icon-pack="fas"
            icon-right="chevron-right"
            class="is-pulled-right"
            data-cy="ratifyButton"
            @click.prevent="enableShowConfirmation">
            Ratificar
          </b-button>
          <div
            v-if="showConfirmation"
            data-cy="confirmation-dialog"
            class="is-flex-desktop is-align-items-center is-pulled-right">
            <p class="is-pulled-left mr-4">¿Seguro quieres ratificar la verificación?</p>
            <b-button type="is-danger" data-cy="button-confirm" @click="ratify">Sí</b-button>
            <b-button type="is-text" data-cy="button-cancel" @click="cancelRatification">
              No
            </b-button>
          </div>
        </section>
      </main>
    </div>
    <Spinner v-if="isLoadingEvaluation" />
  </div>
</template>

<script>
import EvaluationBreadcrumb from '@/ui/components/internal/evaluations/EvaluationBreadcrumb.vue';
import EvaluationHeader from '@/ui/components/internal/evaluations/EvaluationHeader.vue';
import Spinner from '@/ui/components/Spinner.vue';
import Resume from '@/ui/components/internal/evaluations/verify/Resume.vue';
import { BffEvaluationsClient } from '@/application/services/evaluations/apis/bff-evaluations-client';
import { mapMutations, mapState } from 'vuex';
import '@/ui/assets/css/custom-components.css';
import { EligibilityService } from '@/application/services/evaluations/eligibility-service';
import { EligibilityClient } from '@/application/services/evaluations/apis/eligibility-client';
import { BreadcrumbService } from '@/application/services/evaluations/breadcrumb-service';
import { EvaluationService } from '@/application/services/evaluations/evaluation-service';
import { EvaluationParamsCreator } from '@/application/services/evaluations/evaluation-params-creator';
import { EvaluationRetriever } from '@/application/services/evaluations/evaluation-retriever';
import { EvaluationSaver } from '@/application/services/evaluations/evaluation-saver';
import { EvaluationsClient } from '@/application/services/evaluations/apis/evaluations-client';
import { EvaluationRatifier } from '@/application/services/evaluations/evaluation-ratifier';
import { EvaluationDataProcessor } from '@/application/services/evaluations/evaluation-data-processor';
import { formatDate } from '@/infrastructure/format';
import { evaluationsBackend } from '@/application/api';
import { authenticationService } from '@/application/services/api-clients/login-service';

export default {
  name: 'EvaluationSummary',
  components: {
    EvaluationBreadcrumb,
    EvaluationHeader,
    Resume,
    Spinner
  },
  data() {
    return {
      evaluation: {},
      isLoadingEvaluation: true,
      showConfirmation: false,
      links: [],
      bffEvaluationsClient: new BffEvaluationsClient(evaluationsBackend, authenticationService),
      evidenceData: [],
      evidence: {
        Preaprobacion: 'Carta Preaprobacion',
        Simulacion: 'Simulacion'
      },
      eligibilityService: new EligibilityService(
        this.$store,
        new EligibilityClient(evaluationsBackend, authenticationService)
      ),
      evaluationService: new EvaluationService(
        this.$store,
        this.$router,
        new EvaluationParamsCreator(),
        new EvaluationRetriever(new BffEvaluationsClient(evaluationsBackend, authenticationService)),
        new EvaluationSaver(new EvaluationsClient(evaluationsBackend, authenticationService)),
        new EvaluationRatifier(this.$router, new BffEvaluationsClient(evaluationsBackend, authenticationService)),
        new EvaluationDataProcessor(this.$store)
      ),
      breadcrumbService: new BreadcrumbService()
    };
  },
  computed: {
    ...mapState('evaluation', ['parentEvaluationId', 'debtor'])
  },
  async mounted() {
    try {
      await this.fetchEvaluation();
      this.setEligibility();
    } catch (error) {
      this.$log.error(`No se pudo obtener la evaluación: ${error.message}`);
    } finally {
      this.isLoadingEvaluation = false;
      this.setLinksForBreadcrumb();
    }
  },
  methods: {
    ...mapMutations('evaluation', ['setEvaluationParams', 'resetState']),
    formatDate,
    setLinksForBreadcrumb() {
      const fullName = this.$getSafe(this.debtor, 'fullName');
      this.links = this.breadcrumbService.getLinks(this.getParentEvaluationId(), fullName, 'evaluation-summary');
    },
    getParentEvaluationId() {
      const { evaluationId, evaluationType } = this.$route.params;
      return evaluationType === 'automatic' ? evaluationId : this.evaluation.parentEvaluationId;
    },
    getLetterByName(evidences) {
      const evidencesFiltered = evidences.filter(Boolean);

      return evidencesFiltered.map(document => {
        const [prefijo, second] = document.pdfFileName.split('-');
        const prefix = prefijo === 'Carta' ? second : prefijo;
        return { ...document, pdfFileName: this.evidence[prefix], prefix };
      }).filter(({ prefix }) => this.evidence[prefix]);
    },
    setEligibility() {
      const evaluationEligibility = this.$getSafe(this.evaluation, 'eligibility');
      const { eligibility = {}, policyCheckResults = {} } = evaluationEligibility || {};
      const elgibilityParams = this.evaluation.eligibilityParams;
      this.eligibilityService.setEligibility(policyCheckResults, eligibility, elgibilityParams);
    },
    async fetchEvaluation() {
      await this.resetState();
      let evaluationsParams = {};
      try {
        const { evaluationId, evaluationType } = this.$route.params;

        const userId = this.$store.state.currentUser.user._id;
        this.evaluation = await this.evaluationService.getEvaluation(evaluationId);
        const { idNumber } = this.evaluation;

        evaluationsParams = { userId, evaluationId, idNumber };
        const evaluationClient = new BffEvaluationsClient(evaluationsBackend, authenticationService);

        const response = await evaluationClient.getEvaluationsParamsByIdNumber(idNumber, evaluationId);
        const { initialData } = response.data;

        evaluationsParams = { ...evaluationsParams, ...initialData };
        await this.evaluationService.getSimulation(evaluationId, evaluationType);
        await this.evaluationService.getDebtor(idNumber);

        const reports = this.$getSafe(this.debtor, 'reports');

        const evidences = reports ? [...reports] : [];
        this.evidenceData = this.getLetterByName(evidences);
      } catch (error) {
        this.$log.error(`Error fetching evaluation params: ${error.message}`);
      } finally {
        await this.setEvaluationParams(evaluationsParams);
        this.isLoadingEvaluation = false;
      }
    },
    isReadyForRatify() {
      return this.evaluation.result === 'Approved' && this.evaluation.type === 'automatic' && this.evaluation.status !== 'RATIFIED' && this.showConfirmation === false;
    },
    enableShowConfirmation() {
      this.showConfirmation = true;
    },
    cancelRatification() {
      this.showConfirmation = false;
    },
    async ratify() {
      // TODO: poner loading
      const { evaluationId } = this.$route.params;
      try {
        this.isLoadingEvaluation = true;
        const ratify = await this.evaluationService.saver.setAutomaticRatify(evaluationId);

        if (ratify.data.result === true) {
          await this.$router.push({
            name: 'evaluation-detail',
            params: { evaluationId }
          });
        } else {
          this.printMessage(`${ratify.data.message}`);
        }
      } catch (error) {
        this.printMessage(`No se pudo ratificar la evaluación ${error.message}`);
      } finally {
        this.isLoadingEvaluation = false;
      }
    },
    printMessage(message) {
      this.$buefy.toast.open({
        position: 'is-bottom-left',
        message,
        type: 'is-danger',
        duration: 5000
      });
    }
  }
};
</script>

<style scoped>
#evaluation-steps{
  width: 100%;
  -webkit-box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.02);
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.02);
  border-radius: 10px;
}
#evaluation-steps .summary-header {
  border-radius: 10px 10px 0 0;
  background-color: #F9F9F9;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1rem 2rem;
}
#evaluation-panel #summary-client .is-bg-transparent{
  background: transparent;
  color: var(--primary);
}
#evaluations-resume {
  border-radius: 0 0 10px 10px;
  padding: 2rem 2.5rem;
  background-color: #FFF;
}
@media only screen and (max-width: 768px) {
  #evaluation-steps .summary-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
}
</style>
<style>
.field .has-addons a {
  height: 30px;
}
.field-label label{
  font-size: 0.875rem !important;
  font-weight: 400;
}
#evaluations-resume input,
#evaluations-resume textarea,
#evaluations-resume select {
  background-color: #FFFFFF !important;
  border-color: #ECEFF4;
}
@media screen and (min-width: 992px) {
  .field-label label{
    text-align: left;
  }
}
</style>
